// OSS resource donations (arc 101 slice 10, #269 — `docs/idea/donations.md`).
//
// A donor gives one of three things to open source: MONEY (a $50/mo `oss_donor`
// subscription — they bring their own box + keys, run OSS queues only), COMPUTE
// (a donated BYOVM box), or an LLM KEY (a donated BYOK inference key). The
// donated box/key is earmarked `'oss'` and the assigner's hard fence keeps it
// off any private work (`earmarkPermits`). This is the ATTRIBUTION record the
// donor dashboards + the public backer walls read; the hot-path fence reads the
// denormalized `earmark` flag, never these rows. Pure types — no I/O.

import { z } from 'zod';

/** What was donated. */
export const DonationKind = {
  /** An `oss_donor` ($50/mo) subscription; no box/key ref (donor brings both). */
  Money: 'money',
  /** A donated BYOVM runner (`runnerId`), earmarked `'oss'`. */
  Compute: 'compute',
  /** A donated BYOK inference key (`byokKeyId`), earmarked `'oss'`. */
  Llm: 'llm',
} as const;
export const DonationKindSchema = z.enum(DonationKind);
export type DonationKind = z.infer<typeof DonationKindSchema>;

/** Who the gift is pinned to. */
export const DonationTarget = {
  /** One specific project — a `targetRepoId` pin (+ its account for attribution). */
  Project: 'project',
  /** The general OSS pool — "wherever it's needed". */
  Pool: 'pool',
} as const;
export const DonationTargetSchema = z.enum(DonationTarget);
export type DonationTarget = z.infer<typeof DonationTargetSchema>;

/**
 * A donation row (`donations` table). Ids are raw UUIDs (box/key/repo refs too) —
 * the same alphabet the `box`/`billing` surfaces already emit. `targetAccountId`
 * is the pinned project's account when `targetKind = 'project'`, null for the
 * pool. `runnerId`/`byokKeyId` name the donated resource for compute/llm gifts
 * (null for money). `active` is the live/detached bit — a donor may revoke at any
 * time (`revokedAt` stamped), which parks the row and clears the resource's
 * earmark.
 */
export const DonationSchema = z
  .object({
    id: z.string(),
    donorAccountId: z.string(),
    targetKind: DonationTargetSchema,
    targetAccountId: z.string().nullable(),
    /**
     * The REPO the gift is pinned to (arc 101 slice 02) — set for `project`, null
     * for `pool`. `accounts.github_login` is unique at ORG level, so one account
     * covers every repo under `github.com/acme`: pinning to the account alone makes
     * two projects share one backer wall and one gift. `targetAccountId` stays
     * beside it for billing attribution; THIS is what makes "the repo is tied to
     * you because you own it on GitHub" true.
     *
     * The `project` ⇒ set invariant below holds AT REST too: the column's FK
     * cascades with the repo (like `targetAccountId` with the account), so a
     * deleted repo takes its pinned gift with it rather than leaving a `project`
     * row this schema would reject.
     */
    targetRepoId: z.string().nullable(),
    kind: DonationKindSchema,
    runnerId: z.string().nullable(),
    byokKeyId: z.string().nullable(),
    /**
     * The RECIPIENT's model pick for THIS gift (arc 101 slice 03), bounded by the
     * donated key's `allowedModels` ceiling. On the donation, not the key: the
     * donor owns the key, the maintainer owns the choice, and two projects fed by
     * the same donated key may legitimately pick differently. Null = unpicked.
     * Revocation PARKS the row rather than deleting it, so re-attaching restores
     * the pick.
     */
    chosenModel: z.string().nullable(),
    active: z.boolean(),
    createdAt: z.date(),
    revokedAt: z.date().nullable(),
  })
  // Cross-field invariants — the resource ref must match the gift kind, and the
  // target refs must match the target kind. Blocks impossible rows (`money` + a
  // box/key ref, `compute`/`llm` missing its ref, `project` without an account or
  // a repo, `pool` with either) before they can be written or trusted.
  .superRefine((d, ctx) => {
    if (d.kind === DonationKind.Money) {
      if (d.runnerId !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['runnerId'],
          message: 'money donation carries no runnerId',
        });
      }
      if (d.byokKeyId !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['byokKeyId'],
          message: 'money donation carries no byokKeyId',
        });
      }
    } else if (d.kind === DonationKind.Compute) {
      if (d.runnerId === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['runnerId'],
          message: 'compute donation requires a runnerId',
        });
      }
      if (d.byokKeyId !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['byokKeyId'],
          message: 'compute donation carries no byokKeyId',
        });
      }
    } else if (d.kind === DonationKind.Llm) {
      if (d.byokKeyId === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['byokKeyId'],
          message: 'llm donation requires a byokKeyId',
        });
      }
      if (d.runnerId !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['runnerId'],
          message: 'llm donation carries no runnerId',
        });
      }
    }
    if (d.targetKind === DonationTarget.Project && d.targetAccountId === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['targetAccountId'],
        message: 'project donation requires a targetAccountId',
      });
    }
    if (d.targetKind === DonationTarget.Pool && d.targetAccountId !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['targetAccountId'],
        message: 'pool donation has no targetAccountId',
      });
    }
    // The repo pin is the account pin's twin (arc 101 slice 02): a `project` gift
    // names ONE repo, the pool names none. Without this pair an org-level account
    // pin would silently spread one gift across every repo it owns.
    if (d.targetKind === DonationTarget.Project && d.targetRepoId === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['targetRepoId'],
        message: 'project donation requires a targetRepoId',
      });
    }
    if (d.targetKind === DonationTarget.Pool && d.targetRepoId !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['targetRepoId'],
        message: 'pool donation has no targetRepoId',
      });
    }
  });
export type Donation = z.infer<typeof DonationSchema>;

/**
 * Public OSS project stats — what `GET /v1/public/oss/:owner/:repo/stats`, the
 * marketing project pages, and the README badges render. Audit-derived from the
 * `billable_events` ledger (handled issues / PRs) plus the live backer count off
 * `donations`. This shape is the donation half of the route body, present only
 * for a DONATABLE repo — enabled, PUBLIC, and opted in (arc 101 slice 02); a repo
 * admitted only by the public-stats arm (the owner's `public_stats_enabled`
 * consent) gets `full_name` and `agent_authored` instead (#5660, owner ruling
 * 2026-10-09). The endpoint is no-auth, so a private repo's activity must
 * never surface through it.
 *
 * `window` is the current `YYYY-MM` UTC month (matches the billing window). The
 * counts are deduped units — one per handled issue/PR — so they line up with the
 * account's usage rollup. `backers` is the count of DISTINCT donor accounts with
 * a live donation pinned to THIS REPO (not its account — an org's two projects
 * are two pages and two walls); `aiDevsBacking` is how many donated boxes (live
 * `compute` donations) currently back it.
 */
export const OssProjectStatsSchema = z.object({
  fullName: z.string(),
  window: z.string(),
  issuesThisMonth: z.number().int().nonnegative(),
  prsThisMonth: z.number().int().nonnegative(),
  handledThisMonth: z.number().int().nonnegative(),
  handledAllTime: z.number().int().nonnegative(),
  backers: z.number().int().nonnegative(),
  aiDevsBacking: z.number().int().nonnegative(),
});
export type OssProjectStats = z.infer<typeof OssProjectStatsSchema>;
